// i18n: TODO wire translations — all user-facing strings here are plain English
// literals for now. The main thread owns the i18n JSON files and will localize later.

// Survey lifecycle statuses. Kept as plain string values so they persist cleanly
// to localStorage / a real backend later.
export const SURVEY_STATUS = {
  DRAFT: "draft",
  ACTIVE: "active",
  CLOSED: "closed",
};

export const STATUS_META = {
  [SURVEY_STATUS.DRAFT]: { labelKey: "surveyManagement.status_draft", color: "default" },
  [SURVEY_STATUS.ACTIVE]: { labelKey: "surveyManagement.status_active", color: "green" },
  [SURVEY_STATUS.CLOSED]: { labelKey: "surveyManagement.status_closed", color: "red" },
};

export const STATUS_FILTERS = [
  { key: "all", labelKey: "surveyManagement.status_all" },
  { key: SURVEY_STATUS.DRAFT, labelKey: "surveyManagement.status_draft" },
  { key: SURVEY_STATUS.ACTIVE, labelKey: "surveyManagement.status_active" },
  { key: SURVEY_STATUS.CLOSED, labelKey: "surveyManagement.status_closed" },
];

// Supported question types for the survey builder.
export const QUESTION_TYPE = {
  RATING: "rating",
  SINGLE_CHOICE: "single_choice",
  MULTIPLE_CHOICE: "multiple_choice",
  OPEN_TEXT: "open_text",
  PHONE: "phone",
  EMAIL: "email",
  URL: "url",
  LOCATION: "location",
  VIDEO: "video",
};

// An open-text question is a long answer (big box, "A few sentences is enough.", 1,500 characters)
// unless it is set to a short, single-line one.
export const OPEN_TEXT_LENGTH = {
  LONG: "long",
  SHORT: "short",
};

// Rating sub-scales (a single-rating question can be a 1-5 scale or an NPS 0-10).
export const RATING_SCALE = {
  ONE_TO_FIVE: "1-5",
  NPS: "nps",
};

export const RATING_SCALE_META = {
  [RATING_SCALE.ONE_TO_FIVE]: { labelKey: "surveyManagement.scale_1_5", min: 1, max: 5 },
  [RATING_SCALE.NPS]: { labelKey: "surveyManagement.scale_nps", min: 0, max: 10 },
};

export const QUESTION_TYPE_META = {
  [QUESTION_TYPE.RATING]: {
    labelKey: "surveyManagement.type_rating",
    hasOptions: false,
    hasScale: true,
  },
  [QUESTION_TYPE.SINGLE_CHOICE]: {
    labelKey: "surveyManagement.type_single_choice",
    hasOptions: true,
    hasScale: false,
  },
  [QUESTION_TYPE.MULTIPLE_CHOICE]: {
    labelKey: "surveyManagement.type_multiple_choice",
    hasOptions: true,
    hasScale: false,
  },
  [QUESTION_TYPE.OPEN_TEXT]: {
    labelKey: "surveyManagement.type_open_text",
    hasOptions: false,
    hasScale: false,
  },
  [QUESTION_TYPE.PHONE]: {
    labelKey: "surveyManagement.type_phone",
    hasOptions: false,
    hasScale: false,
  },
  [QUESTION_TYPE.LOCATION]: {
    labelKey: "surveyManagement.type_location",
    hasOptions: false,
    hasScale: false,
  },
  [QUESTION_TYPE.EMAIL]: {
    labelKey: "surveyManagement.type_email",
    hasOptions: false,
    hasScale: false,
  },
  // A web address; urlDomain ("bigfive-test.com") limits it to one site.
  [QUESTION_TYPE.URL]: {
    labelKey: "surveyManagement.type_url",
    hasOptions: false,
    hasScale: false,
  },
  // Only offered while video answers are on for the organisation (GET /instruments features).
  [QUESTION_TYPE.VIDEO]: {
    labelKey: "surveyManagement.type_video",
    hasOptions: false,
    hasScale: false,
  },
};

export const QUESTION_TYPE_OPTIONS = Object.entries(QUESTION_TYPE_META).map(
  ([value, meta]) => ({ value, labelKey: meta.labelKey })
);

// ── Distribution ("Distribute" step) ─────────────────────────────────────────
// A survey can be reached through one or more channels. Each channel's config
// shape mirrors the backend distribution_channels.config jsonb, so this maps 1:1
// to the PUT /feedback/instruments/:id/channels payload once surveys are backend
// backed (see surveyService TODO(real-api)).

// The channel cards shown at the top of the Distribute step, in display order.
export const DISTRIBUTION_CHANNELS = [
  { key: "link_qr", icon: "link" },
  { key: "web_embed", icon: "code" },
  { key: "triggered_send", icon: "bolt" },
  { key: "phone_call", icon: "phone" },
  { key: "in_app_sdk", icon: "mobile", comingSoon: true },
  // The AI agent asking the survey on the customer's site or app (inbound voice).
  { key: "voice_widget", icon: "mic", comingSoon: true },
];

// How people answer a survey: a written form, or speaking with the AI voice agent (Pro).
export const SURVEY_RESPONSE_MODE = {
  TEXT: "text",
  VOICE: "voice",
};

// A voice survey reaches people only through the AI agent; a written one only as a form.
const VOICE_CHANNEL_KEYS = ["phone_call", "voice_widget"];

export const channelsForResponseMode = (mode) =>
  DISTRIBUTION_CHANNELS.filter(
    (ch) => VOICE_CHANNEL_KEYS.includes(ch.key) === (mode === SURVEY_RESPONSE_MODE.VOICE)
  );

export const EMBED_TRIGGER_TYPES = [
  "time_on_page",
  "scroll_depth",
  "exit_intent",
  "url_match",
];

export const EMBED_TRIGGER_PRESET = {
  PAGE_LOAD: "page_load",
  LAUNCHER_CLICK: "launcher_click",
  CUSTOM: "custom",
};

export const EMBED_LAUNCHER_POSITION = {
  RIGHT: "right",
  LEFT: "left",
};

export const EMBED_DEVICE_TARGET = {
  ALL: "all",
  MOBILE: "mobile",
  DESKTOP: "desktop",
};

export const SURVEY_FLOW_MODE = {
  STEPPED: "stepped",
  SINGLE_PAGE: "single_page",
};

export const DEFAULT_CALLING_HOURS = { start: "10:00", end: "20:00", daysOff: [6], skipHolidays: true };

// Calls by phone (a test call, the CRM trigger) are shown but switched off: no account can place
// calls yet.
export const PHONE_CALLS_LOCKED = true;

export const DEFAULT_PHONE_TRIGGER = {
  enabled: false,
  delayHours: 48,
  maxAttempts: 2,
  retryAfterHours: 3,
  frequencyDays: 30,
};

// Default per-channel config for a brand-new survey. Everything is disabled so
// the author opts in explicitly; enabling a card reveals its config panel.
export const makeDefaultDistribution = () => ({
  link_qr: {
    enabled: false,
    slug: "",
    fullUrl: "",
    languageMode: "auto",
    fixedLanguage: "en",
    // Kiosk mode key (pivony-api survey_kiosk); switched on its own endpoint, never saved with the survey.
    kioskKey: "",
  },
  web_embed: {
    enabled: false,
    displayMode: "popup",
    triggerPreset: EMBED_TRIGGER_PRESET.LAUNCHER_CLICK,
    oncePerDay: false,
    deviceTarget: EMBED_DEVICE_TARGET.ALL,
    triggerRules: [{ rule_type: "time_on_page", value: 15 }],
    urlMatch: "",
    launcherLabel: "",
    launcherPosition: EMBED_LAUNCHER_POSITION.RIGHT,
  },
  triggered_send: {
    enabled: false,
    eventSource: "sonic",
    eventName: "",
    delayMinutes: 0,
    freqMax: 1,
    freqDays: 30,
    channelForSend: "email",
  },
  // AI phone survey: an ElevenLabs agent calls an uploaded list and asks the questions.
  phone_call: {
    enabled: false,
    exists: false,
    language: "",
    persona: "friendly_empathetic",
    context: "",
    greeting: "",
    // Said before the first question in every call, and the answers to customers' usual questions.
    disclosure: "",
    knowledge: "",
    // The survey's own voice and brand; empty uses the default voice and the org's name.
    voiceId: "",
    brandName: "",
    // When calls may go out (lists and CRM events alike); days are 0 = Monday … 6 = Sunday.
    callingHours: { ...DEFAULT_CALLING_HOURS },
    // A CRM event (e.g. a checkout) calls the customer after delayHours.
    trigger: { ...DEFAULT_PHONE_TRIGGER },
  },
  in_app_sdk: { enabled: false },
});
